import {
  CalendarViewOptions,
  FilterDocument,
  FilterOptionKeys,
  GroupByMode,
  ID,
  TaskGroupBy,
} from '@griply/types';
import { defaultAllTasksFilterDocument } from '@griply/utils';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import {
  useSmartListCustomOrderByGrouping,
  useSmartListFilterDraft,
  useSmartListOnReorder,
  useSortedSmartLists,
  useUpdateSmartList,
} from 'features/smart-lists';
import {
  getDefaultFilterDocument,
  getFilterDocument,
  useSortedTags,
  useUpdateTag,
} from 'features/tags';
import {
  useAllTasksFilterDocument,
  useInboxFilterDocument,
  useUpdateAllTasksFilterDocument,
  useUpdateInboxFilterDocument,
  useUpdateUserSettings,
} from 'features/user';
import React, { Suspense, useCallback, useMemo, useRef } from 'react';
import { XCircle } from 'shared/assets/icons/svgs';
import { FilterButton } from 'shared/components/ui/filter-button';
import { Colors, IconButton } from 'shared/components/ui/icon-button';
import { PageLoader } from 'shared/components/ui/page-loader';
import { FilterDocuments } from 'shared/constants';
import { useAllTasksCustomOrderByGrouping } from 'shared/hooks/use-all-tasks-custom-order-by-grouping';
import { useAllTasksOnReorder } from 'shared/hooks/use-all-tasks-on-reorder';
import { useAllTasksOnSectionCollapse } from 'shared/hooks/use-all-tasks-on-section-collapse';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useUser } from 'shared/hooks/use-user';
import { targetIsInput } from 'shared/utils/target-is-input';
import { ProvidedTaskFilterList } from 'shared/views/task-filter-list';

import { useSelectedCalendarView } from '../../hooks/use-selected-calendar-view';
import {
  DefaultSideOptions,
  DefaultViewMode,
  ExtraSideOptions,
  ExtraViewMode,
  ViewMode,
} from '../../types/side-panel';
import * as Styled from './side-panel.style';
import {
  SidePanelViewSelector,
  SidePanelViewSelectorHandle,
} from './side-panel-view-selector';

const supportedGroupByModes: TaskGroupBy[] = [
  GroupByMode.None,
  GroupByMode.Goal,
  GroupByMode.LifeArea,
  GroupByMode.Priority,
  GroupByMode.Type,
  GroupByMode.Deadline,
  GroupByMode.DeadlineDeadline,
  GroupByMode.Tag,
];

const supportedFilters = Object.values(FilterOptionKeys).filter(
  (filter) => filter !== FilterOptionKeys.CompletedAt,
);

export type SidePanelProps = {
  onClose: () => void;
};

export const SidePanel: React.FC<SidePanelProps> = ({ onClose }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const selectedView = useSelectedCalendarView();
  const viewSelectorRef = useRef<SidePanelViewSelectorHandle>(null);

  const user = useUser();
  const { isMac } = useElectron();

  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const goals = useSortedGoals();
  const tags = useSortedTags();
  const smartLists = useSortedSmartLists({ excludeFrozen: true });

  const updateUserSettings = useUpdateUserSettings();
  const { submit: updateTag } = useUpdateTag();
  const { submit: updateSmartList } = useUpdateSmartList();

  // Filter Documents & Hooks
  const allTasksFilterDocument = useAllTasksFilterDocument();
  const updateAllTasksFilterDocument = useUpdateAllTasksFilterDocument();
  const inboxFilterDocument = useInboxFilterDocument();
  const updateInboxFilterDocument = useUpdateInboxFilterDocument();

  // View Switching State
  const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
    const list = user?.settings?.upcoming?.calendarList;

    if (list === DefaultSideOptions.Inbox) {
      return { type: DefaultSideOptions.Inbox };
    }
    if (list === DefaultSideOptions.AllTasks) {
      return { type: DefaultSideOptions.AllTasks };
    }

    // Try to find if the ID belongs to a smart list
    const smartList = smartLists.find(({ id }) => id === list);
    if (smartList) {
      return {
        type: ExtraSideOptions.SmartList,
        id: smartList.id,
        name: smartList.name,
      };
    }

    // Try to find if the ID belongs to a tag
    const tag = tags.find(({ id }) => id === list);
    if (tag) {
      return {
        type: ExtraSideOptions.Tag,
        id: tag.id,
        name: tag.name,
      };
    }

    return { type: DefaultSideOptions.AllTasks };
  });

  const viewModeId = (viewMode as ExtraViewMode).id;

  const selectedSmartList = useMemo(
    () => smartLists.find(({ id }) => id === viewModeId),
    [smartLists, viewModeId],
  );

  const selectedTag = useMemo(
    () => tags.find(({ id }) => id === viewModeId),
    [tags, viewModeId],
  );

  const smartListDraft = useSmartListFilterDraft(selectedSmartList);

  const filterDocument = useMemo(
    () =>
      viewMode.type === ExtraSideOptions.Tag
        ? getFilterDocument(selectedTag)
        : viewMode.type === ExtraSideOptions.SmartList
          ? smartListDraft.activeFilterDocument ??
            smartListDraft.defaultFilterDocument
          : viewMode.type === DefaultSideOptions.Inbox
            ? inboxFilterDocument
            : allTasksFilterDocument,
    [
      allTasksFilterDocument,
      inboxFilterDocument,
      selectedTag,
      smartListDraft.activeFilterDocument,
      smartListDraft.defaultFilterDocument,
      viewMode.type,
    ],
  );

  const updateTagFilterDocument = useCallback(
    (newFilterDocument: FilterDocument) =>
      selectedTag &&
      updateTag({
        ...selectedTag,
        filterDocument: newFilterDocument,
      }),
    [selectedTag, updateTag],
  );

  const updateFilterDocument =
    viewMode.type === ExtraSideOptions.Tag
      ? updateTagFilterDocument
      : viewMode.type === ExtraSideOptions.SmartList
        ? smartListDraft.updateFilterDocument
        : viewMode.type === DefaultSideOptions.Inbox
          ? updateInboxFilterDocument
          : updateAllTasksFilterDocument;

  // Custom Order Logic
  const allTasksCustomOrder = useAllTasksCustomOrderByGrouping(
    allTasksFilterDocument.groupByMode,
  );
  const updateAllTasksCustomOrder = useAllTasksOnReorder(
    allTasksFilterDocument.groupByMode,
  );

  const smartListCustomOrder = useSmartListCustomOrderByGrouping(
    filterDocument.groupByMode,
    selectedSmartList,
  );
  const updateSmartListCustomOrder = useSmartListOnReorder(
    filterDocument,
    selectedSmartList,
  );

  const customOrder =
    viewMode.type === DefaultSideOptions.AllTasks
      ? allTasksCustomOrder
      : viewMode.type === ExtraSideOptions.SmartList
        ? smartListCustomOrder
        : {};

  const updateCustomOrder =
    viewMode.type === DefaultSideOptions.AllTasks
      ? updateAllTasksCustomOrder
      : viewMode.type === ExtraSideOptions.SmartList
        ? updateSmartListCustomOrder
        : undefined;

  const updateAllTasksCollapsedSections = useAllTasksOnSectionCollapse();

  const updateCollapsedSections = useCallback(
    (collapsedSections: ID[]) => {
      if (viewMode.type === ExtraSideOptions.SmartList) {
        if (!selectedSmartList) {
          return;
        }
        updateSmartList({
          id: selectedSmartList.id,
          filterDocument: {
            ...selectedSmartList.filterDocument,
            collapsedSections,
          },
        });
        return;
      }
      if (viewMode.type === DefaultSideOptions.AllTasks) {
        updateAllTasksCollapsedSections(collapsedSections);
      }
    },
    [
      viewMode.type,
      selectedSmartList,
      updateSmartList,
      updateAllTasksCollapsedSections,
    ],
  );

  const currentDefaultFilterDocument = useMemo(
    () =>
      viewMode.type === ExtraSideOptions.Tag
        ? getDefaultFilterDocument(selectedTag)
        : viewMode.type === ExtraSideOptions.SmartList
          ? smartListDraft.defaultFilterDocument
          : viewMode.type === DefaultSideOptions.AllTasks
            ? defaultAllTasksFilterDocument
            : FilterDocuments.Inbox.defaultFilterDocument,
    [selectedTag, smartListDraft.defaultFilterDocument, viewMode.type],
  );

  const onReset = useCallback(
    () =>
      viewMode.type === ExtraSideOptions.SmartList
        ? smartListDraft.reset()
        : updateFilterDocument(currentDefaultFilterDocument),
    [
      currentDefaultFilterDocument,
      smartListDraft,
      updateFilterDocument,
      viewMode.type,
    ],
  );

  const currentViewValue = useMemo(() => {
    return [DefaultSideOptions.Inbox, DefaultSideOptions.AllTasks].includes(
      (viewMode as DefaultViewMode).type,
    )
      ? viewMode.type
      : (viewMode as ExtraViewMode).id;
  }, [viewMode]);

  const handleViewChange = (value: DefaultSideOptions | ID) => {
    if (!value) {
      return;
    }

    updateUserSettings({ upcoming: { calendarList: value } });

    if (
      [DefaultSideOptions.Inbox, DefaultSideOptions.AllTasks].includes(
        value as DefaultSideOptions,
      )
    ) {
      setViewMode({
        type: value as DefaultSideOptions,
      });
      return;
    }

    const smartList = smartLists.find(({ id }) => id === value);
    if (smartList) {
      setViewMode({
        type: ExtraSideOptions.SmartList,
        id: smartList.id,
        name: smartList.name,
      });
      return;
    }

    const tag = tags.find(({ id }) => id === value);
    if (!tag) {
      return;
    }
    setViewMode({
      type: ExtraSideOptions.Tag,
      id: tag.id,
      name: tag.name,
    });
  };

  useKeysDown(
    [['KeyS']],
    (e) => {
      if (targetIsInput(e.target)) {
        return;
      }

      viewSelectorRef.current?.open();
    },
    { otherKeysInCombinationAllowed: true },
  );

  return (
    <Styled.Container>
      <Styled.Header $isElectronMac={isMac}>
        <SidePanelViewSelector
          ref={viewSelectorRef}
          tags={tags}
          smartLists={smartLists}
          value={currentViewValue}
          onChange={handleViewChange}
        />

        <Styled.HeaderInteractions>
          <FilterButton
            filterDocument={filterDocument}
            defaultFilterDocument={currentDefaultFilterDocument}
            supportedFilters={supportedFilters}
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={updateFilterDocument}
            supportedGroupByModes={supportedGroupByModes}
            onReset={onReset}
            onUpdateSmartList={
              viewMode.type === ExtraSideOptions.SmartList
                ? smartListDraft.save
                : undefined
            }
            renderAsIconButton
            preventDrag
          />
          <IconButton
            icon={XCircle}
            onClick={onClose}
            color={Colors.Default}
            preventDrag
          />
        </Styled.HeaderInteractions>
      </Styled.Header>

      <Styled.Content ref={scrollRef}>
        <Suspense fallback={<PageLoader />}>
          <ProvidedTaskFilterList
            // Force re-render when switching views to ensure clean state
            key={
              viewMode.type === ExtraSideOptions.Tag
                ? `tag-${viewMode.id}`
                : viewMode.type
            }
            filterDocument={filterDocument}
            defaultFilterDocument={currentDefaultFilterDocument}
            onReset={onReset}
            customOrder={customOrder}
            onReorder={updateCustomOrder}
            onSectionCollapse={updateCollapsedSections}
            scrollElement={scrollRef}
            includeDuration={selectedView !== CalendarViewOptions.Month}
            denseFormat
          />
        </Suspense>
      </Styled.Content>
    </Styled.Container>
  );
};
